import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { VotingList } from "../../domain/vote/Models/VotingList"
import {
  PUBLIC_VOTING_STATUS_ENUM,
  VOTED_FILTER_ENUM,
  VOTING_SCOPE_ENUM,
  VOTING_TYPE_ENUM,
  OrderDirectionEnum,
} from "@antipartido-inc/contracts-ifaces"
import { DomainEventDetail, DomainEvents } from "../../domain/_kernel/Events"
import { TransactionType } from "../../domain/ethereum/Models/Transaction"
import { Pagination } from "../../domain/_kernel/Pagination"

const PAGE_SIZE = 10

export const FILTERS_STORAGE_KEY = "voting-list-filters"

export const DEFAULT_FILTERS: VotingFilters = {
  statuses: [PUBLIC_VOTING_STATUS_ENUM.ACTIVE],
  type: undefined,
  votedFilter: VOTED_FILTER_ENUM.NOT_VOTED,
}

export interface VotingFilters {
  readonly statuses: readonly PUBLIC_VOTING_STATUS_ENUM[]
  readonly type?: VOTING_TYPE_ENUM
  readonly votedFilter?: VOTED_FILTER_ENUM
}

interface VotingListState {
  readonly votingList?: VotingList
  readonly isLoading: boolean
  readonly isLoadingMore: boolean
  readonly totalElements: number
  readonly baseTotalElements: number
  readonly error?: string
  readonly _isListening: boolean
  readonly filters: VotingFilters
}

interface VotingListActions {
  fetchVotingList: (params?: {
    statuses?: PUBLIC_VOTING_STATUS_ENUM[]
    scope?: VOTING_SCOPE_ENUM
    type?: VOTING_TYPE_ENUM
    search?: string
    startDateFrom?: string
    startDateTo?: string
    pagination?: Pagination
    votedFilter?: VOTED_FILTER_ENUM
    sinceRegistration?: boolean
    // TODO [BACKEND]: Añadir parámetro includeAutoUpdatable para incluir votaciones
    // isAutoUpdatable independientemente de sinceRegistration. Pendiente de backend.
  }) => Promise<VotingList | undefined>
  fetchMoreVotings: () => Promise<void>
  setFilters: (filters: VotingFilters) => Promise<VotingList | undefined>
  reset: () => void
  startListening: () => void
  stopListening: () => void
}

type VotingListStore = VotingListState & VotingListActions

export const useVotingListStore = create<VotingListStore>()(
  devtools(
    (set, get) => {
      const handleSuccessTransaction = (e: CustomEvent<DomainEventDetail[DomainEvents.SUCCESS_TRANSACTION]>) => {
        if (
          e.detail.transaction.type === TransactionType.VOTE ||
          e.detail.transaction.type === TransactionType.VOTE_EDIT
        ) {
          const { filters } = get()
          get().fetchVotingList({
            statuses: filters.statuses.length > 0 ? [...filters.statuses] : undefined,
            type: filters.type,
            sinceRegistration: true,
          })
        }
      }

      return {
        votingList: undefined,
        isLoading: false,
        isLoadingMore: false,
        totalElements: 0,
        baseTotalElements: 0,
        error: undefined,
        _isListening: false,
        filters: DEFAULT_FILTERS,

        fetchVotingList: async (params) => {
          const state = get()
          if (state.isLoading) return undefined

          set({ isLoading: true, error: undefined })

          const { filters } = state
          const pagination = Pagination.create({
            offset: 0,
            limit: PAGE_SIZE,
            orderBy: "endDate",
            orderDirection: OrderDirectionEnum.DESC,
          })
          const mergedStatuses = params?.statuses ?? (filters.statuses.length > 0 ? [...filters.statuses] : undefined)
          const mergedVotedFilter = params?.votedFilter ?? filters.votedFilter
          const mergedParams = {
            ...params,
            statuses: mergedStatuses,
            type: params?.type ?? filters.type,
            votedFilter: mergedVotedFilter,
            pagination,
            sinceRegistration: true,
          }

          try {
            const { votingList } = await window.domain.GetVoteListVoteUseCase.execute(mergedParams)
            const total = votingList.pagination.numElements ?? 0
            const isDefaultFilters =
              mergedStatuses?.length === 1 &&
              mergedStatuses[0] === PUBLIC_VOTING_STATUS_ENUM.ACTIVE &&
              !mergedParams.type &&
              mergedVotedFilter === VOTED_FILTER_ENUM.NOT_VOTED
            set({
              votingList,
              totalElements: total,
              ...(isDefaultFilters && { baseTotalElements: total }),
              isLoading: false,
            })
            return votingList
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            set({ error: message, isLoading: false })
          }
        },

        fetchMoreVotings: async () => {
          const { votingList, totalElements, isLoadingMore, filters } = get()
          if (isLoadingMore || !votingList) return
          if (votingList.items.length >= totalElements) return

          set({ isLoadingMore: true })

          const pagination = Pagination.create({
            offset: votingList.items.length,
            limit: PAGE_SIZE,
            orderBy: "endDate",
            orderDirection: OrderDirectionEnum.DESC,
          })

          try {
            const { votingList: nextPage } = await window.domain.GetVoteListVoteUseCase.execute({
              statuses: filters.statuses.length > 0 ? [...filters.statuses] : undefined,
              type: filters.type,
              votedFilter: filters.votedFilter,
              pagination,
              sinceRegistration: true,
            })
            const mergedItems = [...votingList.items, ...nextPage.items]
            const mergedList = VotingList.create({ items: mergedItems, pagination: nextPage.pagination })
            set({
              votingList: mergedList,
              totalElements: nextPage.pagination.numElements ?? totalElements,
              isLoadingMore: false,
            })
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            set({ error: message, isLoadingMore: false })
          }
        },

        setFilters: async (filters) => {
          set({ filters })
          return get().fetchVotingList({
            statuses: filters.statuses.length > 0 ? [...filters.statuses] : undefined,
            type: filters.type,
            votedFilter: filters.votedFilter,
          })
        },

        reset: () => {
          set({
            votingList: undefined,
            isLoading: false,
            isLoadingMore: false,
            totalElements: 0,
            baseTotalElements: 0,
            error: undefined,
            filters: DEFAULT_FILTERS,
          })
        },

        startListening: () => {
          if (get()._isListening) return
          set({ _isListening: true })
          window.addEventListener(DomainEvents.SUCCESS_TRANSACTION, handleSuccessTransaction as EventListener)
        },

        stopListening: () => {
          if (!get()._isListening) return
          set({ _isListening: false })
          window.removeEventListener(DomainEvents.SUCCESS_TRANSACTION, handleSuccessTransaction as EventListener)
        },
      }
    },
    { name: "VotingListStore" },
  ),
)
